import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Plus, 
  MessageSquare, 
  Clock, 
  AlertCircle, 
  CheckCircle,
  HelpCircle,
  Send,
  RefreshCw,
  AlertTriangle,
  Users,
  Loader2
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from 'date-fns';

interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_response' | 'resolved' | 'closed';
  queue_position: number | null;
  category: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  user_id?: string;
  seller_id?: string;
}

interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_role: 'seller' | 'admin' | 'buyer';
  message: string;
  created_at: string;
}

interface SupportCenterProps {
  userType: 'buyer' | 'seller';
}

export function SupportCenter({ userType }: SupportCenterProps) {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    priority: "medium" as const,
    category: "other" as const
  });

  // Fetch tickets
  const fetchTickets = async () => {
    if (!user) return;
    
    try {
      // Filter based on user type
      const { data, error } = userType === 'seller' 
        ? await supabase
            .from('support_tickets')
            .select('*')
            .eq('seller_id', user.id)
            .order('created_at', { ascending: false })
        : await supabase
            .from('support_tickets')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets((data || []) as SupportTicket[]);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: "Error",
        description: "Failed to load support tickets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for a ticket
  const fetchMessages = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages((data || []) as SupportMessage[]);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Create new ticket
  const createTicket = async () => {
    if (!user || !newTicket.subject || !newTicket.description) return;
    
    setSubmitting(true);
    try {
      const ticketData: any = {
        subject: newTicket.subject,
        description: newTicket.description,
        priority: newTicket.priority,
        category: newTicket.category
      };
      
      // Set appropriate user field based on type
      if (userType === 'seller') {
        ticketData.seller_id = user.id;
      } else {
        ticketData.user_id = user.id;
      }
      
      const { data, error } = await supabase
        .from('support_tickets')
        .insert(ticketData)
        .select()
        .single();

      if (error) throw error;
      
      setTickets([data as SupportTicket, ...tickets]);
      setNewTicket({ subject: "", description: "", priority: "medium", category: "other" });
      toast({
        title: "Success",
        description: `Ticket created. Queue position: #${data.queue_position}`,
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: "Error",
        description: "Failed to create support ticket",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!user || !selectedTicket || !messageText.trim()) return;
    
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          ticket_id: selectedTicket.id,
          sender_id: user.id,
          sender_role: userType === 'seller' ? 'seller' : 'buyer',
          message: messageText
        })
        .select()
        .single();

      if (error) throw error;
      
      setMessages([...messages, data as SupportMessage]);
      setMessageText("");
      
      // Update ticket status if it was waiting for response
      if (selectedTicket.status === 'waiting_response') {
        await supabase
          .from('support_tickets')
          .update({ status: 'in_progress' })
          .eq('id', selectedTicket.id);
        
        setSelectedTicket({ ...selectedTicket, status: 'in_progress' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    
    // Subscribe to real-time updates
    const filterColumn = userType === 'seller' ? 'seller_id' : 'user_id';
    const channel = supabase
      .channel('support-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'support_tickets',
        filter: `${filterColumn}=eq.${user?.id}`
      }, () => {
        fetchTickets();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_messages'
      }, (payload) => {
        if (selectedTicket && payload.new && 
            (payload.new as any).ticket_id === selectedTicket.id) {
          setMessages(prev => [...prev, payload.new as SupportMessage]);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedTicket, userType]);

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);
    }
  }, [selectedTicket]);

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4" />;
      case 'high': return <AlertCircle className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
      case 'closed': return <CheckCircle className="h-4 w-4" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4" />;
      case 'waiting_response': return <MessageSquare className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
      case 'closed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'waiting_response': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getCategoryOptions = () => {
    if (userType === 'seller') {
      return [
        { value: 'technical', label: 'Technical Issue' },
        { value: 'billing', label: 'Billing & Payments' },
        { value: 'product', label: 'Product Management' },
        { value: 'shipping', label: 'Shipping & Delivery' },
        { value: 'account', label: 'Account Settings' },
        { value: 'other', label: 'Other' }
      ];
    } else {
      return [
        { value: 'order', label: 'Order Issue' },
        { value: 'payment', label: 'Payment Problem' },
        { value: 'delivery', label: 'Delivery & Shipping' },
        { value: 'product', label: 'Product Question' },
        { value: 'refund', label: 'Refund Request' },
        { value: 'account', label: 'Account Help' },
        { value: 'other', label: 'Other' }
      ];
    }
  };

  const openTickets = tickets.filter(t => ['open', 'in_progress', 'waiting_response'].includes(t.status));
  const resolvedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status));

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openTickets.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Avg. Response Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2.5h</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Resolved This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Queue Position</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {openTickets[0]?.queue_position ? `#${openTickets[0].queue_position}` : 'N/A'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Queue Alert */}
      {openTickets.some(t => t.queue_position && t.queue_position <= 3) && (
        <Alert>
          <Users className="h-4 w-4" />
          <AlertDescription>
            Your ticket is near the front of the queue! An admin will assist you shortly.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Support Center</CardTitle>
              <CardDescription>Get help from our admin team</CardDescription>
            </div>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="gradient">
                  <Plus className="h-4 w-4 mr-2" />
                  New Ticket
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Support Ticket</DialogTitle>
                  <DialogDescription>
                    Describe your issue and we'll get back to you as soon as possible
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium">Subject</label>
                    <Input
                      placeholder="Brief description of your issue"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <Select
                      value={newTicket.category}
                      onValueChange={(value: any) => setNewTicket({ ...newTicket, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getCategoryOptions().map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Priority</label>
                    <Select
                      value={newTicket.priority}
                      onValueChange={(value: any) => setNewTicket({ ...newTicket, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Textarea
                      placeholder="Provide detailed information about your issue..."
                      rows={5}
                      value={newTicket.description}
                      onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    />
                  </div>
                  
                  <Button 
                    onClick={createTicket} 
                    disabled={submitting || !newTicket.subject || !newTicket.description}
                    className="w-full"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Ticket'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="open">
            <TabsList className="mb-4 w-full overflow-x-auto flex justify-start">
              <TabsTrigger value="open" className="whitespace-nowrap flex-shrink-0">
                Open ({openTickets.length})
              </TabsTrigger>
              <TabsTrigger value="resolved" className="whitespace-nowrap flex-shrink-0">
                Resolved ({resolvedTickets.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="open">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : openTickets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No open tickets
                </div>
              ) : (
                <div className="space-y-4">
                  {openTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{ticket.subject}</h3>
                            <Badge className={`${getPriorityColor(ticket.priority)} text-white`}>
                              {getPriorityIcon(ticket.priority)}
                              <span className="ml-1 capitalize">{ticket.priority}</span>
                            </Badge>
                            <Badge className={`${getStatusColor(ticket.status)} text-white`}>
                              {getStatusIcon(ticket.status)}
                              <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                            </Badge>
                            {ticket.queue_position && (
                              <Badge variant="outline">
                                Queue: #{ticket.queue_position}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {ticket.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Created {formatDistanceToNow(new Date(ticket.created_at))} ago</span>
                            {ticket.category && (
                              <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="resolved">
              {resolvedTickets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No resolved tickets
                </div>
              ) : (
                <div className="space-y-4">
                  {resolvedTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{ticket.subject}</h3>
                            <Badge className="bg-green-500 text-white">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Resolved
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {ticket.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Resolved {formatDistanceToNow(new Date(ticket.resolved_at!))} ago</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Message Dialog */}
      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedTicket.subject}</DialogTitle>
              <DialogDescription>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`${getPriorityColor(selectedTicket.priority)} text-white`}>
                    {selectedTicket.priority}
                  </Badge>
                  <Badge className={`${getStatusColor(selectedTicket.status)} text-white`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </Badge>
                  {selectedTicket.queue_position && (
                    <Badge variant="outline">Queue: #{selectedTicket.queue_position}</Badge>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4">
              <div className="bg-muted p-3 rounded-lg mb-4">
                <p className="text-sm">{selectedTicket.description}</p>
              </div>
              
              <ScrollArea className="h-[300px] border rounded-lg p-4">
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground">
                    No messages yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_role === userType ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            msg.sender_role === userType
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {formatDistanceToNow(new Date(msg.created_at))} ago
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              
              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2 mt-4">
                  <Input
                    placeholder="Type your message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  />
                  <Button onClick={sendMessage} disabled={submitting || !messageText.trim()}>
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}